import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

import { TIMELINE_BANDS, TIMELINE_MILESTONES } from '@/constants/platform'

const MONTHS = Array.from({ length: 12 }, (_, index) => `M${String(index + 1).padStart(2, '0')}`)

const percent = (month: number): string => `${(month / 12) * 100}%`

const describe = (segments: Array<[number, number]>): string => segments
    .map(([start, end]) => `month ${Math.floor(start) + 1} to month ${Math.ceil(end)}`)
    .join(' and ')

/**
 * A twelve-month strip with one band per capability. Each band draws in from the left when the
 * strip scrolls into view, and the four milestones drop in above it.
 */
export const CapabilityTimeline: FC = (): ReactNode => {
    const [ref, inView] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -15% 0px' })

    return (
        <div ref={ref} data-reveal={inView ? 'in' : 'pre'} className='w-full'>
            <div className='desk:grid desk:grid-cols-[12rem_minmax(0,1fr)] desk:gap-8'>
                <span className='hidden desk:block' aria-hidden='true' />

                <div className='relative h-14' aria-hidden='true'>
                    {TIMELINE_MILESTONES.map((milestone, index) => {
                        const edge = milestone.month >= 12 ? 'right' : milestone.month <= 0 ? 'left' : 'center'

                        return (
                            <span
                                key={milestone.label}
                                className='reveal-cell absolute bottom-0 flex flex-col'
                                style={{
                                    left: percent(milestone.month),
                                    transform: edge === 'right' ? 'translateX(-100%)' : edge === 'center' ? 'translateX(-50%)' : undefined,
                                    alignItems: edge === 'right' ? 'flex-end' : edge === 'center' ? 'center' : 'flex-start',
                                    '--delay': `${900 + index * 140}ms`
                                } as CSSProperties}
                            >
                                <span className='hud whitespace-nowrap text-ink'>
                                    {milestone.label}
                                </span>

                                <span className='mt-2 size-2 bg-signal' />
                            </span>
                        )
                    })}
                </div>
            </div>

            <ol className='mt-3 border-t border-hairline'>
                {TIMELINE_BANDS.map((band, index) => (
                    <li key={band.id} className='grid gap-3 border-b border-hairline py-5 desk:grid-cols-[12rem_minmax(0,1fr)] desk:items-center desk:gap-8'>
                        <div>
                            <p className='hud text-ink'>
                                {band.name}
                            </p>

                            <p className='hud text-mute'>
                                {band.note}
                            </p>
                        </div>

                        <div className='relative h-6'>
                            <span className='absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-hairline' aria-hidden='true' />

                            {band.segments.map(([start, end]) => (
                                <span
                                    key={`${start}-${end}`}
                                    className={cn('draw-x absolute top-1/2 -translate-y-1/2', band.id === 'archive' ? 'h-px bg-ink/70' : 'h-2 bg-ink')}
                                    style={{ left: percent(start), width: percent(end - start), '--delay': `${index * 180 + start * 40}ms` } as CSSProperties}
                                    aria-hidden='true'
                                />
                            ))}

                            {TIMELINE_MILESTONES.map(milestone => (
                                <span key={milestone.label} className='absolute inset-y-0 w-px bg-hairline' style={{ left: percent(milestone.month) }} aria-hidden='true' />
                            ))}

                            <span className='sr-only'>
                                {band.name}: {describe(band.segments)}.
                            </span>
                        </div>
                    </li>
                ))}
            </ol>

            <div className='desk:grid desk:grid-cols-[12rem_minmax(0,1fr)] desk:gap-8' aria-hidden='true'>
                <span className='hidden desk:block' />

                <div className='grid grid-cols-12 pt-3'>
                    {MONTHS.map((month, index) => (
                        <span key={month} className={cn('font-mono text-[10px] tracking-[0.08em] text-mute', index % 2 === 1 && 'hidden sm:block')}>
                            {month}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )
}
